import React, { Fragment, useState, useEffect } from 'react';
import { FormDateInput, FormHelperText, FormNumericInput, FormSelect } from "components/form-fields";
import { useValidation } from "hooks/useValidation";
import PropTypes from 'prop-types';
import { NotificationInline } from "components/notifications";
import moment from "moment";
import classNames from 'classnames';
import 'components/settings/settings.css';

export function PolicyAdditionalSettings(
    {
        disabled = false,
        saving = false,
        reoccurrenceSettingsDisabled = false,
        editing,
        loading,
        onSettingsUpdate,
        onValidationUpdate,
        policy,
        reoccurrenceSettingsDisabledNotification
    }
) {
    const dataRetentionUnits = [
        { id: "Days", name: "Days" },
        { id: "Months", name: "Months" },
        { id: "Years", name: "Years" }
    ]

    const [isValid, errors, validate] = useValidation();
    const [loadingValue, setLoading] = useState(true);
    const [policySettings, setPolicySettings] = useState(
        {
            startDate: "",
            endDate: "",
            dataRetention: null
        });

    useEffect(onValidationChange, [isValid]);

    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(() => {
        setPolicySettings(c => ({ ...c, ...policy }));
    }, [policy]);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    function onstartDateChange(item) {
        var updatedPolicySettings = {
            ...policySettings,
            startDate: item ? moment(item).format("YYYY-MM-DD") : null
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    function onendDateChange(item) {
        var updatedPolicySettings = {
            ...policySettings,
            endDate: item ? moment(item).format("YYYY-MM-DD") : null
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    useEffect(updateValidationRules, [policy])

    function updateValidationRules() {
        let rules = [];

        if (!editing) {
            rules.push({ fieldName: "startDate", required: true }, { fieldName: "dataRetentionPeriod", min: 0, max: 1000, required: !!policy.dataRetention }, { fieldName: "dataRetentionUnit", required: !!policy.dataRetention });
        }

        validate(rules, {
            ...policy,
            dataRetentionPeriod: policy.dataRetention?.dataRetentionPeriod,
            dataRetentionUnit: policy.dataRetention?.dataRetentionUnit
        });
    }

    const setDataRetentionPeriod = (period) => {
        const dataRetentionClone = period ? { dataRetentionPeriod: period, dataRetentionUnit: policy.dataRetention?.dataRetentionUnit } : null;
        const clone = {
            ...policy,
            dataRetention: dataRetentionClone
        };

        onSettingsUpdate(clone);
    }

    const setDataRetentionUnit = (unit) => {
        const dataRetentionClone =  { dataRetentionPeriod: policy.dataRetention?.dataRetentionPeriod || 1, dataRetentionUnit: unit.id };
        const clone = {
            ...policy,
            dataRetention: dataRetentionClone
        };

        onSettingsUpdate(clone);
    }

    const dataRetentionError = errors["dataRetentionPeriod"] || (errors["dataRetentionUnit"] && "A period is required");

    return (
        <Fragment>
            <FormDateInput
                id="policy-start-date"
                disabled={disabled || reoccurrenceSettingsDisabled || saving}
                headingText="Start date"
                onChange={onstartDateChange}
                value={policySettings.startDate}
                loading={loadingValue}
                dangerHelperText={errors.startDate}
                minDate={moment("2000-01-01").toDate()}
                maxDate={policySettings.endDate ? moment(policySettings.endDate).toDate() : null}
            ></FormDateInput>
            <NotificationInline
                id="notification-settings-disabled"
                loading={loadingValue}
                show={reoccurrenceSettingsDisabled}
                text={reoccurrenceSettingsDisabledNotification}
                intent="info">
            </NotificationInline>
            <FormDateInput
                id="policy-end-date"
                disabled={disabled || saving}
                headingText="End date"
                onChange={onendDateChange}
                value={policySettings.endDate}
                loading={loadingValue}
                dangerHelperText={errors.endDate}
                minDate={policySettings.startDate ? moment(policySettings.startDate).add(1, 'day').toDate() : moment("2000-01-01").toDate()}
            ></FormDateInput>
            <div className={classNames('form-row field-combination spacer-bottom-small', { 'outer-validation': !!dataRetentionError })}>
                <h4>Data retention period</h4>
                <FormHelperText loading={loadingValue}>If the period is left blank the default retention period of 30 months will be applied</FormHelperText>
                <FormNumericInput
                    disabled={disabled || saving}
                    disabledInput={disabled || saving}
                    min={0}
                    max={1000}
                    selectedValue={policy.dataRetention?.dataRetentionPeriod}
                    onValueChange={setDataRetentionPeriod}
                    loading={loadingValue}
                    placeholder="0"
                ></FormNumericInput>
                <FormSelect
                    disabled={disabled || saving}
                    items={dataRetentionUnits}
                    onItemSelect={setDataRetentionUnit}
                    selectedValue={policy.dataRetention?.dataRetentionUnit}
                    loading={loadingValue}
                    placeholder="Choose a period"
                ></FormSelect>
                <FormHelperText loading={loadingValue} danger={true}>{dataRetentionError}</FormHelperText>
            </div>
        </Fragment>
    );
}

PolicyAdditionalSettings.propTypes = {
    policy: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    saving: PropTypes.bool,
    reoccurrenceSettingsDisabled: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    onSettingsUpdate: PropTypes.func.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    reoccurrenceSettingsDisabledNotification: PropTypes.string
};